import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { getMceEvents } from '@app/api/routes/mce/mceEvents';
import type { MceEventsFilters, PaginatedMceEventsResponse } from '@app/api/routes/mce/mceEvents.types';

export const MCE_EVENTS_QUERY_KEY = ['mce-events'] as const;

/**
 * Récupère la liste centralisée des événements MCE par pagination
 * infinie (saisies de formulaires, pointages PDS/FDS, et à terme rondes).
 *
 * `keepPreviousData` évite le flash de chargement entre les changements
 * de filtres ; `getNextPageParam` retourne `undefined` quand la dernière
 * page est atteinte, ce qui désactive le load more.
 */
export const useGetMceEvents = (filters?: MceEventsFilters) =>
  useInfiniteQuery<
    PaginatedMceEventsResponse,
    Error,
    { pages: PaginatedMceEventsResponse[]; pageParams: number[] },
    readonly [...typeof MCE_EVENTS_QUERY_KEY, MceEventsFilters | undefined],
    number
  >({
    queryKey: [...MCE_EVENTS_QUERY_KEY, filters] as const,
    queryFn: ({ pageParam }) => getMceEvents({ ...filters, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page ? lastPage.meta.current_page + 1 : undefined,
    // L'invalidation socket (validatePdsFds) couvre déjà la fraîcheur.
    // staleTime de 30s évite un refetch à chaque navigation back/forward
    // tout en gardant un filet de sécurité si le socket plante.
    staleTime: 30_000,
    placeholderData: keepPreviousData
  });
